import React, { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useAbility } from '@casl/react';
import moment from 'moment';
import camelCaseRecursive from 'camelcase-keys-recursive';
import { ComponentDataTable, DefaultModal, Icon, ButtonTooltip } from '../../index';
import { indexEmployeeDncRequest, exportEmployeeDncRequest } from '../../../requests/dncs';
import { downloadFile } from '../../../services/utils';
import { AbilityContext } from '../../../config/abilityContext';
import getColumns from './TalentDncColumns';

const TalentDncDataTable = ({ employeeId }) => {
  const dispatch = useDispatch();
  const ability = useAbility(AbilityContext);
  const [employeeDncs, setEmployeeDncs] = useState([]);
  const [amount, setAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState({});

  const [modalShow, setModalShow] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalBody, setModalBody] = useState('');
  const [modalAction, setModalAction] = useState(() => null);

  const defaultDates = useMemo(
    () => ({
      start: moment().startOf('year'),
      end: moment().endOf('year')
    }),
    []
  );

  const columns = useMemo(() => getColumns(), []);

  const handleIndexRequest = params => {
    setLoading(true);
    const requestParams = {
      ...params,
      filter_employee: employeeId
    };
    setQuery(requestParams);
    indexEmployeeDncRequest({
      dispatch,
      params: requestParams,
      successCallback: res => {
        const responseData = camelCaseRecursive(res.data);
        const { data, metadata } = responseData;

        setEmployeeDncs(data || []);
        setAmount(metadata?.amount || 0);
      },
      callback: () => setLoading(false)
    });
  };

  const handleDownload = () => {
    setLoading(true);
    exportEmployeeDncRequest({
      dispatch,
      params: query,
      responseType: 'text',
      successCallback: res => {
        setModalShow(false);
        downloadFile(res);
      },
      callback: () => setLoading(false)
    });
  };

  const handleMassAction = action => {
    if (action === 'export') {
      setModalTitle('Exportar DNC');
      setModalBody('¿Estás seguro que deseas descargar el listado de DNC?');
      setModalAction(() => handleDownload);
      setModalShow(true);
    }
  };

  const handleModalClose = () => {
    setModalShow(false);
  };

  return (
    <div className="mx-3 mt-3">
      <div className="data-table-yellow">
        <ComponentDataTable
          resourceRequest={handleIndexRequest}
          onRequest={loading}
          columns={columns}
          data={employeeDncs}
          totalRows={amount}
          noDataComponentText="No hay registros de DNC en este periodo."
          withDateSelect
          withStartDate
          withEndDate
          defaultStartDate={defaultDates.start}
          defaultEndDate={defaultDates.end}
          withMassActions
          massActions={
            <>
              {ability.can('export', 'EmployeeDnc') && (
                <ButtonTooltip
                  variant="circle-primary"
                  className="mr-2 mb-3 btn-circle"
                  text="Exportar"
                  onClick={() => handleMassAction('export')}
                >
                  <Icon icon="download-2" />
                </ButtonTooltip>
              )}
            </>
          }
        />
      </div>

      <DefaultModal
        title={modalTitle}
        body={modalBody}
        show={modalShow}
        handleClose={handleModalClose}
        handleConfirm={() => modalAction()}
        disabled={loading}
        variantBtnClose="outline-info"
        variantBtnSave="primary"
      />
    </div>
  );
};

export default TalentDncDataTable;
